import { isMobileBreakpoint } from '@theme/utilities';

class HotspotExplorer extends HTMLElement {
  #controller;
  #activeViewIndex = 0;
  #activeHotspotIndex = 0;
  #isNavigating = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Tab clicks
    this.querySelectorAll('.hotspot-explorer__tab').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        this.#switchView(parseInt(e.currentTarget.dataset.viewIndex, 10));
      }, { signal });
    });

    // Hotspot point clicks
    this.querySelectorAll('.hotspot-explorer__point').forEach((point) => {
      point.addEventListener('click', (e) => {
        e.stopPropagation();
        const viewIdx = parseInt(e.currentTarget.dataset.viewIndex, 10);
        const hotspotIdx = parseInt(e.currentTarget.dataset.hotspotIndex, 10);

        if (isMobileBreakpoint()) {
          this.#navigateCard(viewIdx, hotspotIdx);
        } else {
          this.#setActiveFeature(viewIdx, hotspotIdx);
        }
      }, { signal });
    });

    // Feature card clicks (desktop)
    this.querySelectorAll('.hotspot-explorer__feature').forEach((feature) => {
      feature.addEventListener('click', (e) => {
        const viewIdx = parseInt(e.currentTarget.dataset.viewIndex, 10);
        const hotspotIdx = parseInt(e.currentTarget.dataset.hotspotIndex, 10);
        this.#setActiveFeature(viewIdx, hotspotIdx);
      }, { signal });
    });

    // Dot clicks (mobile) — gallery-class buttons inside dots container
    this.querySelectorAll('.hotspot-explorer__carousel-dots .slideshow-control').forEach((dot) => {
      dot.addEventListener('click', (e) => {
        const viewIdx = parseInt(e.currentTarget.dataset.viewIndex, 10);
        const hotspotIdx = parseInt(e.currentTarget.dataset.hotspotIndex, 10);
        this.#navigateCard(viewIdx, hotspotIdx);
      }, { signal });
    });

    // Card clicks (mobile) — tap peeking card to navigate to it
    this.querySelectorAll('.hotspot-explorer__carousel-card').forEach((card) => {
      card.addEventListener('click', (e) => {
        const hotspotIdx = parseInt(e.currentTarget.dataset.hotspotIndex, 10);
        if (hotspotIdx !== this.#activeHotspotIndex) {
          this.#navigateCard(this.#activeViewIndex, hotspotIdx);
        }
      }, { signal });
    });

    // Scroll listener on tracks — sync dots, hotspot points during swipe
    this.querySelectorAll('.hotspot-explorer__carousel-track').forEach((track) => {
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (this.#isNavigating || ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const cards = track.querySelectorAll('.hotspot-explorer__carousel-card');
          const scrollCenter = track.scrollLeft + track.offsetWidth / 2;
          let newIndex = 0;
          cards.forEach((card, i) => {
            const cardCenter = card.offsetLeft + card.offsetWidth / 2;
            if (Math.abs(cardCenter - scrollCenter) < Math.abs(
              (cards[newIndex]?.offsetLeft || 0) + (cards[newIndex]?.offsetWidth || 0) / 2 - scrollCenter
            )) {
              newIndex = i;
            }
          });
          const viewIdx = parseInt(track.dataset.viewIndex, 10);
          if (newIndex !== this.#activeHotspotIndex) {
            this.#activeHotspotIndex = newIndex;
            this.#setActivePoint(viewIdx, newIndex);
            this.#updateActiveDot(viewIdx, newIndex);
            this.#updateActiveCard(viewIdx, newIndex);
          }
          ticking = false;
        });
      }, { signal, passive: true });
    });

    // Resize handler
    window.addEventListener('resize', () => {
      if (!isMobileBreakpoint()) {
        this.#setActiveFeature(this.#activeViewIndex, this.#activeHotspotIndex);
      }
    }, { signal });

    // Desktop: auto-select first hotspot; Mobile: init card viewer
    if (!isMobileBreakpoint()) {
      this.#setActiveFeature(0, 0);
    } else {
      this.querySelectorAll('.hotspot-explorer__point--active').forEach((el) => {
        el.classList.remove('hotspot-explorer__point--active');
      });
      this.querySelectorAll('.hotspot-explorer__feature--active').forEach((el) => {
        el.classList.remove('hotspot-explorer__feature--active');
      });
      this.#showTrackForView(0);
      this.#setActivePoint(0, 0);
      this.#updateActiveCard(0, 0);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Navigate to a card on mobile via native scroll (gallery-style).
   */
  #navigateCard(viewIdx, hotspotIdx) {
    if (viewIdx !== this.#activeViewIndex) {
      this.#switchView(viewIdx);
    }

    const track = this.querySelector(
      `.hotspot-explorer__carousel-track[data-view-index="${viewIdx}"]`
    );
    if (!track) return;

    const cardCount = parseInt(track.dataset.cardCount, 10) || 0;
    hotspotIdx = Math.max(0, Math.min(hotspotIdx, cardCount - 1));

    const card = track.querySelector(
      `.hotspot-explorer__carousel-card[data-hotspot-index="${hotspotIdx}"]`
    );
    if (!card) return;

    this.#activeHotspotIndex = hotspotIdx;
    this.#setActivePoint(viewIdx, hotspotIdx);
    this.#updateActiveDot(viewIdx, hotspotIdx);
    this.#updateActiveCard(viewIdx, hotspotIdx);

    // Block scroll listener during programmatic scroll to prevent dot flicker
    this.#isNavigating = true;
    // Center the card in the track viewport
    track.scrollTo({
      left: card.offsetLeft + card.offsetWidth / 2 - track.offsetWidth / 2,
      behavior: 'smooth'
    });

    // Re-enable scroll sync after smooth scroll settles
    let scrollEndTimer;
    const onScrollEnd = () => {
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        this.#isNavigating = false;
        track.removeEventListener('scroll', onScrollEnd);
      }, 100);
    };
    track.addEventListener('scroll', onScrollEnd, { passive: true });
  }

  /**
   * Show the correct track and dots group for a view.
   */
  #showTrackForView(viewIndex) {
    this.querySelectorAll('.hotspot-explorer__carousel-track').forEach((track) => {
      const isActive = parseInt(track.dataset.viewIndex, 10) === viewIndex;
      track.classList.toggle('hotspot-explorer__carousel-track--active', isActive);
      if (isActive) {
        track.scrollLeft = 0;
      }
    });
    this.querySelectorAll('.hotspot-explorer__carousel-dots-group').forEach((group) => {
      group.classList.toggle(
        'hotspot-explorer__carousel-dots-group--active',
        parseInt(group.dataset.viewIndex, 10) === viewIndex
      );
    });
  }

  /**
   * Toggle active class on carousel cards for chevron visibility.
   */
  #updateActiveCard(viewIdx, hotspotIdx) {
    const track = this.querySelector(
      `.hotspot-explorer__carousel-track[data-view-index="${viewIdx}"]`
    );
    if (!track) return;
    track.querySelectorAll('.hotspot-explorer__carousel-card').forEach((card) => {
      card.classList.toggle(
        'hotspot-explorer__carousel-card--active',
        parseInt(card.dataset.hotspotIndex, 10) === hotspotIdx
      );
    });
  }

  #setActivePoint(viewIdx, hotspotIdx) {
    this.querySelectorAll(`.hotspot-explorer__point[data-view-index="${viewIdx}"]`).forEach((point) => {
      point.classList.toggle(
        'hotspot-explorer__point--active',
        parseInt(point.dataset.hotspotIndex, 10) === hotspotIdx
      );
    });
  }

  #updateActiveDot(viewIdx, hotspotIdx) {
    this.querySelectorAll(`.hotspot-explorer__carousel-dots .slideshow-control[data-view-index="${viewIdx}"]`).forEach((dot) => {
      dot.setAttribute('aria-selected', parseInt(dot.dataset.hotspotIndex, 10) === hotspotIdx ? 'true' : 'false');
    });
  }

  /**
   * Switch to a different view (tab).
   */
  #switchView(viewIndex) {
    if (viewIndex === this.#activeViewIndex) return;

    this.querySelectorAll('.hotspot-explorer__tab').forEach((tab) => {
      tab.classList.toggle('hotspot-explorer__tab--active', parseInt(tab.dataset.viewIndex, 10) === viewIndex);
    });

    const forward = viewIndex > this.#activeViewIndex;
    const slide = getComputedStyle(this).getPropertyValue('--he-transition-slide').trim() || '15%';
    this.style.setProperty('--he-slide-leave', forward ? `-${slide}` : slide);
    this.style.setProperty('--he-slide-enter', forward ? slide : `-${slide}`);

    this.querySelectorAll('.hotspot-explorer__view').forEach((view) => {
      const idx = parseInt(view.dataset.viewIndex, 10);
      if (idx === this.#activeViewIndex) {
        view.classList.remove('hotspot-explorer__view--active');
        view.classList.add('hotspot-explorer__view--leaving');
      } else if (idx === viewIndex) {
        view.classList.remove('hotspot-explorer__view--leaving');
        view.classList.add('hotspot-explorer__view--active');
      }
    });

    const speed = parseInt(getComputedStyle(this).getPropertyValue('--he-transition-speed'), 10) || 500;
    setTimeout(() => {
      this.querySelectorAll('.hotspot-explorer__view--leaving').forEach((v) => {
        v.classList.remove('hotspot-explorer__view--leaving');
      });
    }, speed + 300);

    this.querySelectorAll('.hotspot-explorer__feature-group').forEach((group) => {
      const isActive = parseInt(group.dataset.viewIndex, 10) === viewIndex;
      group.classList.toggle('hotspot-explorer__feature-group--active', isActive);
      group.classList.remove('hotspot-explorer__feature-group--animating');
      if (isActive) {
        requestAnimationFrame(() => {
          group.classList.add('hotspot-explorer__feature-group--animating');
        });
      }
    });

    this.#activeViewIndex = viewIndex;

    if (!isMobileBreakpoint()) {
      this.#setActiveFeature(viewIndex, 0);
    } else {
      this.#showTrackForView(viewIndex);
      this.#activeHotspotIndex = 0;
      this.#setActivePoint(viewIndex, 0);
      this.#updateActiveDot(viewIndex, 0);
      this.#updateActiveCard(viewIndex, 0);
    }
  }

  /**
   * Activate a specific hotspot point and feature card (desktop).
   * Uses two-phase animation: deactivate old card first (body collapses instantly),
   * then activate new card in next frame (prevents height overlap / stretching).
   */
  #setActiveFeature(viewIdx, hotspotIdx) {
    if (viewIdx !== this.#activeViewIndex) {
      this.#switchView(viewIdx);
    }
    this.#activeHotspotIndex = hotspotIdx;
    this.#setActivePoint(viewIdx, hotspotIdx);

    const cards = this.querySelectorAll(`.hotspot-explorer__feature[data-view-index="${viewIdx}"]`);
    const newCard = this.querySelector(
      `.hotspot-explorer__feature[data-view-index="${viewIdx}"][data-hotspot-index="${hotspotIdx}"]`
    );

    // If clicking the already-active card, do nothing
    if (newCard && newCard.classList.contains('hotspot-explorer__feature--active')) return;

    // Phase 1: deactivate all cards (body collapses at 0s)
    cards.forEach((f) => f.classList.remove('hotspot-explorer__feature--active'));

    // Phase 2: activate new card after body collapse settles
    requestAnimationFrame(() => {
      if (newCard) newCard.classList.add('hotspot-explorer__feature--active');
    });
  }
}

if (!customElements.get('hotspot-explorer')) {
  customElements.define('hotspot-explorer', HotspotExplorer);
}
